import { ReactComponent as LogoImage } from '@assets/svg/icon-logo.svg';
import { ReactComponent as LogoExpandedImage } from '@assets/svg/standard-logo.svg';
import { Profile } from '@components';
import { HelpMenu } from '@components/HelpMenu/HelpMenu';
import { NavigationContext } from '@layout/navigation/navigation';
import { useNavigationState } from '@store/hookstate';
import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SidebarItem } from '@components/SidebarItem/SidebarItem';
import { useSelector } from 'react-redux';
import {
  checkDevice,
  selectDevice,
} from '@store/redux/features/mobile/mobileSelectors';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import { useGetIsMounted } from 'rooks';
import { Bar, Bottom, Logo, Mid, Top } from './BeamOnSidebar.styles';
import { PopoverSidebarItem } from '../PopoverSidebarItem/PopoverSidebarItem';
import { getEnabledPaths } from './logic/getEnabledPaths';

interface MainSidebarBodyProps {
  excludeElementsRef: (arr: HTMLElement[]) => void;
}
export function MainSidebarBody(props: MainSidebarBodyProps) {
  const { isSuperAdmin } = useContext(NavigationContext);
  const navigationState = useNavigationState();
  const { excludeElementsRef } = props;

  const { push } = useHistory();
  const location = useLocation();
  const deviceType = useSelector(selectDevice());
  const { isMobile } = useSelector(checkDevice());

  const paths = getEnabledPaths({
    deviceType,
    isSuperAdmin,
  });

  const isOpen = navigationState.sidebarOpen.get();
  const showClose = isMobile && isOpen;
  const [dropdownRef, setDropdownRef] = useState<HTMLElement | null>(null);
  const getIsMounted = useGetIsMounted();
  const getElements = () => {
    const els: HTMLElement[] = [];
    if (dropdownRef) els.push(dropdownRef);
    return els;
  };

  useEffect(() => {
    if (getIsMounted()) excludeElementsRef(getElements());
  }, [dropdownRef]);

  if (
    !paths.includes('activity') &&
    !paths.includes('products') &&
    !paths.includes('updates') &&
    !paths.includes('tasks') &&
    !paths.includes('onboarding') &&
    !paths.includes('identificationRequests') &&
    !paths.includes('configuration') &&
    !paths.includes('help')
  ) {
    push('/products');
  }

  return (
    <Bar>
      <Top data-open={isOpen}>
        <Logo data-open={isOpen}>
          {isOpen ? (
            <LogoExpandedImage width={100} height={28} />
          ) : (
            <LogoImage width={32} height={28} />
          )}
        </Logo>
        {showClose && <BActionIcon onClick={navigationState.closeNavigation} />}
      </Top>
      <Mid>
        {paths.includes('activity') && (
          <SidebarItem
            isActive={location.pathname.includes('activity')}
            onClick={() => push('/activity')}
            label="Activity"
            icon="history"
          />
        )}
        {paths.includes('products') && (
          <SidebarItem
            isActive={location.pathname.includes('products')}
            onClick={() => push('/products')}
            label="Inventory"
            icon="cube"
          />
        )}
        {paths.includes('updates') && (
          <SidebarItem
            isActive={location.pathname.includes('updates')}
            onClick={() => push('/updates')}
            label="Updates"
            icon="import"
          />
        )}
        {paths.includes('tasks') && (
          <SidebarItem
            isActive={location.pathname.includes('tasks')}
            onClick={() => push('/tasks')}
            label="Tasks"
            icon="tick-circle"
            isOnlySuperAdmin
          />
        )}
        {paths.includes('documents') && (
          <SidebarItem
            isActive={location.pathname.includes('documents')}
            onClick={() => push('/documents')}
            label="Documents"
            icon="document"
          />
        )}
        {paths.includes('onboarding') && (
          <SidebarItem
            isActive={location.pathname.includes('onboarding')}
            onClick={() => push('/onboarding')}
            label="Onboarding"
            icon="th-derived"
          />
        )}

        {paths.includes('identificationRequests') && (
          <SidebarItem
            isActive={location.pathname.includes('identificationRequests')}
            onClick={() => push('/identificationRequests')}
            label="Identification Requests"
            icon="geosearch"
            isOnlySuperAdmin
          />
        )}
      </Mid>

      <Bottom>
        {paths.includes('configuration') && (
          <SidebarItem
            isActive={location.pathname.includes('configuration')}
            onClick={() => push('/configuration')}
            label="Configuration"
            icon="cog"
          />
        )}

        {paths.includes('help') && (
          <PopoverSidebarItem
            dropdownRef={setDropdownRef}
            label="Help"
            icon="help"
            Dialog={HelpMenu}
          />
        )}
        <Profile />
      </Bottom>
    </Bar>
  );
}
